import type {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import {
  HttpErrorResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, tap, throwError } from 'rxjs';
import { SnackbarService } from '../services/snackbar.service';
import { TokenStorageService } from '../services/token-storage.service';

/**
 * Custom error class that carries GraphQL error codes for reliable detection.
 * This allows catchError to use code-based detection (primary) instead of
 * fragile message pattern matching.
 */
class GraphQLErrorWithCodes extends Error {
  constructor(
    message: string,
    public readonly codes: string[]
  ) {
    super(message);
    this.name = 'GraphQLErrorWithCodes';
  }
}

/**
 * Authentication error codes returned by the GraphQL API.
 * These are defined in the backend's error extensions.code field.
 */
const AUTH_ERROR_CODES = [
  'AUTH_NOT_AUTHENTICATED',
  'AUTH_INVALID_TOKEN',
  'AUTH_TOKEN_EXPIRED',
  'AUTH_UNAUTHORIZED'
] as const;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private networkErrorShown = false; // Prevent multiple snackbars for same error

  constructor(
    private readonly tokenService: TokenStorageService,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<Record<string, unknown>>> {
    let authReq = req;
    const token = this.tokenService.getToken();

    if (token && token !== '') {
      authReq = authReq.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        }
      });
    }

    return next.handle(authReq).pipe(
      tap((data: HttpEvent<any>) => {
        const response: HttpResponse<{
          data: any;
          errors: {
            extensions?: {
              code?: string;
            };
            message: string;
          }[];
        }> = data as any; // force cast

        if (response.body?.errors) {
          // Extract error codes from extensions.code (reliable, structured)
          const codes: string[] = response.body.errors
            .map(e => e.extensions?.code)
            .filter((code): code is string => !!code);

          // Extract messages for error reporting
          const messages = response.body.errors.map(e => e.message).join('. ');

          // Primary: Check if any error is an authentication error using code (reliable)
          const hasAuthErrorCode = codes.some(code => AUTH_ERROR_CODES.includes(code as typeof AUTH_ERROR_CODES[number]));

          // Fallback: Check message patterns if no codes available (for backward compatibility)
          const hasAuthErrorMessage = codes.length === 0 && AUTH_ERROR_CODES.some(pattern =>
            messages.toUpperCase().includes(pattern)
          );

          if (hasAuthErrorCode) {
            // Structured error with code - preferred path
            throw new GraphQLErrorWithCodes(messages, codes);
          } else if (hasAuthErrorMessage) {
            // Fallback: Message-based detection (for APIs that don't set extensions.code)
            throw new GraphQLErrorWithCodes(messages, []);
          }
        }
      }),
      catchError((error: Error) => {
        // Handle HTTP 401 Unauthorized
        if (error instanceof HttpErrorResponse && !authReq.url.includes('login') && !authReq.url.includes('zendesk') && error.status === 401) {
          this.tokenService.signOut();
          window.location.href = '/';
        }

        // Handle network errors (backend unreachable, CORS, etc.)
        // Status 0 means the request couldn't reach the server
        if (error instanceof HttpErrorResponse && error.status === 0) {
          console.error('AuthInterceptor: Backend unreachable or network error');
          // Show user-friendly message (only once per error burst)
          if (!this.networkErrorShown) {
            this.networkErrorShown = true;
            this.snackbarService.openSnackBar(
              'Impossible de se connecter au serveur. Vérifiez votre connexion internet et actualisez la page. Si le problème persiste, contactez le support.',
              'Fermer',
              { duration: 10000, panelClass: ['error-snackbar'] }
            );
            // Reset flag after 10 seconds to allow showing again
            setTimeout(() => {
              this.networkErrorShown = false;
            }, 10000);
          }
        }

        // Handle GraphQL authentication errors (thrown from tap above)
        // These are GraphQLErrorWithCodes (preferred) or regular Errors (fallback)
        if (!(error instanceof HttpErrorResponse) && error.message) {
          let isAuthError = false;

          // Primary: Use code-based detection (reliable, structured)
          if (error instanceof GraphQLErrorWithCodes) {
            isAuthError = error.codes.some(code =>
              AUTH_ERROR_CODES.includes(code as typeof AUTH_ERROR_CODES[number])
            );
          }

          // Fallback: Message pattern matching (for backward compatibility with older errors)
          // Only used if codes weren't available
          if (!isAuthError) {
            isAuthError = AUTH_ERROR_CODES.some(pattern =>
              error.message.toUpperCase().includes(pattern)
            );
          }

          if (isAuthError) {
            console.warn('AuthInterceptor: Authentication error detected, redirecting to login');
            this.tokenService.signOut();
            window.location.href = '/';
          }
        }

        return throwError(() => error);
      }));
  }
}
